import React, { useState, useEffect } from 'react';

import './SubmissionStatusIcon.scss';

/**
 * Animated submit-result indicator. Shows a green tick (success) or red cross
 * (fail), each drawn inside a circle that materialises clockwise as it fades
 * in, lingers for 2 seconds, then fades out.
 *
 * Calls `onComplete` once the 2 seconds elapse so the parent can restore the
 * button's idle content and re-enable its onClick (which should be disabled
 * while the icon is showing).
 */
function SubmissionStatusIcon({ variant = 'success', onComplete }) {
  // enter -> exit (fade out) -> done (renders "Submit")
  const [phase, setPhase] = useState('enter');

  useEffect(() => {
    // begin the exit fade shortly before the 2s so it finishes on time
    const exitTimer = setTimeout(() => setPhase('exit'), 1700);
    const doneTimer = setTimeout(() => {
      setPhase('done');
      if (onComplete) onComplete();
    }, 2000);
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);

  // parent restores the idle button content via onComplete once we're done
  if (phase === 'done') return null;

  const isSuccess = variant === 'success';

  return (
    <span
      className={`submission-status-icon ${isSuccess ? 'is-success' : 'is-fail'} ${
        phase === 'exit' ? 'is-exiting' : ''
      }`}
      role="img"
      aria-label={isSuccess ? 'Note submitted' : 'Submission failed'}
    >
      <svg viewBox="0 0 52 52">
        <circle className="ssi-circle" cx="26" cy="26" r="23" />
        {isSuccess ? (
          <path className="ssi-mark" d="M15 27 l7 7 l15 -16" />
        ) : (
          <>
            <path className="ssi-mark" d="M18 18 L34 34" />
            <path className="ssi-mark ssi-mark--second" d="M34 18 L18 34" />
          </>
        )}
      </svg>
    </span>
  );
}

export function SubmissionSuccessIcon(props) {
  return <SubmissionStatusIcon variant="success" {...props} />;
}

export function SubmissionFailIcon(props) {
  return <SubmissionStatusIcon variant="fail" {...props} />;
}

export default SubmissionStatusIcon;
